<template>
  <gas-page-shell :breadcrumb="['报表管理', '自定义报表']">
    <div class="search-panel">
      <el-form :inline="true" :model="query" size="small" label-width="72px" class="search-form">
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="dateRange"
            type="datetimerange"
            range-separator="至"
            start-placeholder="开始时间"
            end-placeholder="结束时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            style="width:380px"
            @change="onDateRangeChange"
          />
        </el-form-item>
        <el-form-item label="站点名称">
          <el-select v-model="query.siteName" clearable filterable placeholder="请选择站点" style="width:160px" @change="onSiteChange">
            <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item label="项目分类">
          <el-select v-model="query.projectCategoryName" clearable filterable placeholder="全部分类" style="width:160px">
            <el-option v-for="c in projectCategoryOptions" :key="c" :label="c" :value="c" />
          </el-select>
        </el-form-item>
        <el-form-item label="设备类型">
          <el-select v-model="query.deviceType" filterable placeholder="请选择设备类型" style="width:160px" @change="onReportDeviceTypeChange">
            <el-option v-for="t in deviceTypeOptions" :key="t" :label="t" :value="t" />
          </el-select>
        </el-form-item>
        <el-form-item label="间隔">
          <el-input-number
            v-model="query.interval"
            :min="1"
            :max="1440"
            :step="1"
            :precision="0"
            controls-position="right"
            placeholder="分钟"
            style="width:120px"
          />
          <span class="interval-hint">分钟</span>
        </el-form-item>
        <el-form-item label="设备名称">
          <el-select
            v-if="query.siteName"
            v-model="query.deviceName"
            clearable
            filterable
            :loading="siteDevicesLoading"
            :placeholder="deviceSelectPlaceholder"
            style="width:220px"
            @change="onSiteDeviceChange"
          >
            <el-option
              v-for="d in siteDeviceOptions"
              :key="d.deviceName + '|' + d.deviceCode"
              :label="d.label"
              :value="d.deviceName"
            />
          </el-select>
          <el-input v-else v-model="query.deviceName" clearable placeholder="设备名称（模糊搜索）" style="width:200px" @keyup.enter.native="search" />
        </el-form-item>
        <el-form-item>
          <el-button @click="resetSearch">重置</el-button>
          <el-button type="primary" @click="search">查询</el-button>
        </el-form-item>
      </el-form>
      <div v-if="siteNeedsDevicePick && !query.deviceName" class="device-pick-tip">
        <i class="el-icon-warning-outline"></i>
        该站点下有多个不同类型的设备，请先选择具体设备，再点击查询
      </div>
    </div>

    <div class="stat-row">
      <div v-for="item in statCards" :key="item.label" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }"><i :class="item.icon"></i></div>
        <div class="stat-body">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value">{{ item.value }}<span v-if="item.unit" class="unit">{{ item.unit }}</span></div>
        </div>
      </div>
    </div>

    <div class="table-panel">
      <div class="table-head">
        <span class="table-title">子设备数据汇总 <em>共 {{ total }} 条记录</em></span>
      </div>
      <el-table
        :data="reportListRows"
        :key="'protocol-' + (query.deviceType || '') + '-' + (query.protocolId || 'none')"
        v-loading="loading"
        border
        size="small"
        class="report-table"
        :row-key="reportListRowKey"
        :row-class-name="reportRowClassName"
        :span-method="reportSpanMethod"
      >
        <el-table-column label="序号" width="60" align="center">
          <template slot-scope="{ row, $index }">{{ isReportSummaryRow(row) ? '' : rowIndex($index, row) }}</template>
        </el-table-column>
        <el-table-column
          column-key="reportDate"
          :label="listHasSampleTime ? '采样时间' : '日期'"
          :width="listHasSampleTime ? 170 : 110"
        >
          <template slot-scope="{ row }">{{ isReportSummaryRow(row) ? '' : periodLabel(row) }}</template>
        </el-table-column>
        <el-table-column column-key="siteName" label="站点名称" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ reportSiteName(row) || '—' }}</template>
        </el-table-column>
        <el-table-column column-key="deviceName" label="设备名称" min-width="140">
          <template slot-scope="{ row }">
            <span v-if="isReportSummaryRow(row)">{{ reportDeviceName(row) }}</span>
            <a v-else class="site-link" href="javascript:;" @click.prevent="openDetail(row)">{{ reportDeviceName(row) }}</a>
          </template>
        </el-table-column>
        <el-table-column v-if="showReportSubDevice" column-key="subDevice" label="子设备" min-width="100" align="center">
          <template slot-scope="{ row }">
            <span v-if="isReportSummaryRow(row)">{{ row._subLabel || '—' }}</span>
            <a v-else class="site-link" href="javascript:;" @click.prevent="openDetail(row)">{{ row._subLabel || '—' }}</a>
          </template>
        </el-table-column>
        <el-table-column column-key="deviceType" prop="deviceType" label="设备类型" width="110" />
        <el-table-column
          v-for="m in activeDeviceMetrics"
          :key="m.key"
          :label="metricColumnLabel(m)"
          :min-width="m.width || 120"
          align="right"
        >
          <template slot-scope="{ row }">{{ formatNum(metricValue(row, m)) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="96" align="center">
          <template slot-scope="{ row }">
            <span v-if="isReportSummaryRow(row)">—</span>
            <gas-status-tag v-else kind="reportRecord" :value="row.recordStatus" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button v-if="!isReportSummaryRow(row)" type="text" size="small" icon="el-icon-view" @click="openDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="canLoadCustomReport"
        class="pager" background layout="total, sizes, prev, pager, next, jumper"
        :total="total" :current-page="query.pageNum" :page-size="query.pageSize"
        :page-sizes="[10, 20, 50, 100]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <report-period-detail-drawer
      :visible.sync="detailVisible"
      mode="custom"
      :title="detailTitle"
      :detail="detail"
      :rows="detailList"
      :metrics="detailMetrics"
      :show-sub-device="showReportSubDevice"
      :total="detailTotal"
      :query="detailQuery"
      :page-sizes="[10, 20, 50, 100]"
      :loading="detailLoading"
      :rows-loading="detailRowsLoading"
      :table-title="query.interval ? '按间隔采样数据' : '每天统计数据'"
      :export-label="query.interval ? '导出按间隔采样数据' : '导出区间每天数据'"
      @size-change="handleDetailSizeChange"
      @current-change="handleDetailCurrentChange"
      @export="exportDetail"
      @closed="onDetailClosed"
    />
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import ReportPeriodDetailDrawer from '@/components/ReportPeriodDetailDrawer.vue'
import reportFlowCommon, { formatNum, recentDateTimeRange } from '@/mixins/reportFlowCommon'
import reportTableMerge from '@/mixins/reportTableMerge'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import siteOptionsMixin from '@/mixins/siteOptions'
import {
  DEFAULT_MONITOR_DEVICE_TYPE,
  formatMetricColumnLabel,
  getDeviceTypeMetrics,
  metricValueFromRow
} from '@/config/deviceTypeMetrics'
import reportProtocolFilter from '@/mixins/reportProtocolFilter'
import { gasApi } from '@/api/gas'

export default {
  components: { GasPageShell, ReportPeriodDetailDrawer },
  mixins: [reportFlowCommon, deviceTypeOptionsMixin, siteOptionsMixin, reportTableMerge, reportProtocolFilter],
  data() {
    const defaultRange = recentDateTimeRange(1)
    return {
      // 后端已按子设备汇总返回（几台子设备几行），前端不再插入小计/合计行
      reportMergeDevice: false,
      dateRange: [...defaultRange],
      detailVisible: false,
      detailLoading: false,
      detailRowsLoading: false,
      detail: null,
      detailList: [],
      detailTotal: 0,
      detailQuery: {
        pageNum: 1,
        pageSize: 10
      },
      projectCategoryOptions: [],
      siteDeviceOptions: [],
      siteDevicesLoading: false,
      query: {
        pageNum: 1,
        pageSize: 10,
        beginDate: defaultRange[0],
        endDate: defaultRange[1],
        projectCategoryName: '',
        deviceType: DEFAULT_MONITOR_DEVICE_TYPE,
        protocolId: null,
        protocolName: '',
        siteName: '',
        deviceName: '',
        interval: 10
      }
    }
  },
  created() {
    this.loadDeviceTypeOptions()
    this.loadSiteOptions()
    this.loadProjectCategoryOptions()
    // 未选择站点/设备前不自动查询，避免全量区间查询慢导致一直转圈
    if (this.hasCustomFilter()) {
      this.loadStats()
      this.loadData()
    }
  },
  computed: {
    canLoadCustomReport() {
      return true
    },
    siteDeviceTypes() {
      return [...new Set((this.siteDeviceOptions || []).map(d => (d.terminalType || '').trim()).filter(Boolean))]
    },
    siteNeedsDevicePick() {
      return !!this.query.siteName && this.siteDeviceOptions.length > 1 && this.siteDeviceTypes.length > 1
    },
    deviceSelectPlaceholder() {
      if (this.siteDevicesLoading) return '设备加载中...'
      if (!this.siteDeviceOptions.length) return '该站点下暂无设备'
      return this.siteNeedsDevicePick ? '请选择具体设备' : '请选择设备'
    },
    activeDeviceMetrics() {
      return this.customProtocolMetrics.length
        ? this.customProtocolMetrics
        : getDeviceTypeMetrics(this.query.deviceType)
    },
    listHasSampleTime() {
      return (this.list || []).some(r => r && r.sampleTime)
    },
    detailMetrics() {
      return this.customProtocolMetrics.length
        ? this.customProtocolMetrics
        : getDeviceTypeMetrics(this.query.deviceType)
    },
    detailTitle() {
      if (!this.detail) return '自定义报表详情'
      const name = this.reportDeviceName(this.detail) || '未命名设备'
      const sub = this.showReportSubDevice ? (this.detail.subDeviceLabel || this.detail._subLabel) : ''
      return sub ? `自定义报表详情 — ${name} / ${sub}` : `自定义报表详情 — ${name}`
    }
  },
  methods: {
    formatNum,
    periodLabel(row) {
      if (!row) return '-'
      const st = row.sampleTime ? String(row.sampleTime) : ''
      const d = (row.reportDate || '').toString()
      if (!st) return d || '-'
      if (st.includes('-')) return st
      const dateOnly = d.includes(' ') ? d.split(' ')[0] : d
      return `${dateOnly} ${st}`
    },
    metricColumnLabel(metric) {
      return formatMetricColumnLabel(metric)
    },
    metricValue(row, metric) {
      return metricValueFromRow(row, metric)
    },
    projectCategoryLabel(row) {
      return typeof row === 'string' ? row : (row && row.categoryName) || ''
    },
    projectCategoryValue(row) {
      return typeof row === 'string' ? row : (row && row.categoryName) || ''
    },
    async loadProjectCategoryOptions() {
      try {
        const res = await gasApi.projectCategory.options()
        const rows = res.data || []
        this.projectCategoryOptions = rows.map(this.projectCategoryValue).filter(Boolean)
      } catch (e) {
        this.projectCategoryOptions = []
      }
    },
    onDateRangeChange(val) {
      if (val && val.length === 2) {
        this.query.beginDate = val[0]
        this.query.endDate = val[1]
      } else {
        this.query.beginDate = ''
        this.query.endDate = ''
      }
    },
    async onSiteChange() {
      this.query.deviceName = ''
      this.query.pageNum = 1
      this.siteDeviceOptions = []
      const siteName = (this.query.siteName || '').trim()
      if (!siteName) return
      this.siteDevicesLoading = true
      try {
        const res = await gasApi.device.list({ siteName, pageNum: 1, pageSize: 500 })
        const records = (res.data && res.data.records) || []
        const seen = new Set()
        const options = []
        records.forEach(r => {
          const name = ((r && (r.deviceName || r.deviceCode)) || '').trim()
          if (!name || seen.has(name)) return
          seen.add(name)
          const type = ((r && r.terminalType) || '').trim()
          options.push({
            deviceName: name,
            deviceCode: (r && r.deviceCode) || '',
            terminalType: type,
            label: type ? `${name}（${type}）` : name
          })
        })
        options.sort((a, b) => a.deviceName.localeCompare(b.deviceName, 'zh-Hans-CN'))
        this.siteDeviceOptions = options
        const types = [...new Set(options.map(d => (d.terminalType || '').trim()).filter(Boolean))]
        if (types.length === 1) this.applyDeviceType({ terminalType: types[0] })
        if (options.length === 1) {
          this.query.deviceName = options[0].deviceName
        }
      } catch (e) {
        this.siteDeviceOptions = []
      } finally {
        this.siteDevicesLoading = false
      }
    },
    onSiteDeviceChange(name) {
      this.query.pageNum = 1
      const hit = (this.siteDeviceOptions || []).find(d => d.deviceName === name)
      if (hit) this.applyDeviceType(hit)
    },
    applyDeviceType(device) {
      const type = ((device && device.terminalType) || '').trim()
      if (!type || type === this.query.deviceType) return
      this.query.deviceType = type
      this.onReportDeviceTypeChange()
    },
    hasCustomFilter() {
      return !!(this.query.siteName || '').trim() || !!(this.query.deviceName || '').trim()
    },
    search() {
      if (!this.hasCustomFilter()) {
        this.$message.warning('请先选择站点名称或设备名称，再点击查询')
        return
      }
      if (this.siteNeedsDevicePick && !this.query.deviceName) {
        this.$message.warning('该站点下有多个不同类型的设备，请先选择具体设备后再查询')
        return
      }
      this.query.pageNum = 1
      this.loadStats()
      this.loadData()
    },
    async loadStats() {
      const res = await gasApi.reportFlowCustom.stats(this.buildQuery())
      const d = res.data || {}
      const cards = [
        { label: '设备数', value: d.totalSites || 0, icon: 'el-icon-s-home', color: '#1677ff', bg: '#e8f3ff' },
        { label: '子设备数', value: d.subDeviceCount || 0, icon: 'el-icon-tickets', color: '#722ed1', bg: '#f9f0ff' }
      ]
      if (this.activeDeviceMetrics.some(m => m.key === 'stdTotal')) {
        cards.push({ label: '区间用气', value: formatNum(d.totalGasUsage), unit: 'm³', icon: 'el-icon-s-opportunity', color: '#ff7d00', bg: '#fff7e8' })
      }
      cards.push(
        { label: '异常设备', value: d.abnormalSites || 0, icon: 'el-icon-warning-outline', color: '#f53f3f', bg: '#ffece8' },
        { label: '查询区间', value: (d.beginDate || '-') + '~' + (d.endDate || '-'), icon: 'el-icon-date', color: '#00b42a', bg: '#e8ffea' }
      )
      this.statCards = cards
    },
    async loadData() {
      if (!this.canLoadCustomReport) {
        this.list = []
        this.total = 0
        return
      }
      this.loading = true
      try {
        const res = await gasApi.reportFlowCustom.list(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    resetSearch() {
      const defaultRange = recentDateTimeRange(1)
      this.dateRange = [...defaultRange]
      this.query = {
        pageNum: 1,
        pageSize: 10,
        beginDate: defaultRange[0],
        endDate: defaultRange[1],
        projectCategoryName: '',
        ...this.defaultReportTypeQuery(),
        siteName: '',
        deviceName: '',
        interval: 10
      }
      this.customProtocolMetrics = []
      this.siteDeviceOptions = []
      // 重置后未选择站点/设备，不自动查询
      this.list = []
      this.total = 0
      this.statCards = []
    },
    async openDetail(row) {
      if (this.isReportSummaryRow(row)) return
      this.detail = { ...row, periodLabel: `${this.query.beginDate || '-'} ~ ${this.query.endDate || '-'}` }
      this.detailVisible = true
      this.detailQuery.pageNum = 1
      this.detailQuery.pageSize = 10
      await this.loadDetail(true)
    },
    detailParams(extra = {}) {
      const d = this.detail || {}
      return {
        beginDate: this.query.beginDate,
        endDate: this.query.endDate,
        regionName: d.regionName,
        siteType: d.siteType,
        siteName: this.reportDeviceName(d) || d.siteName || this.query.siteName,
        reportType: d.reportType,
        deviceId: d.deviceId,
        terminalCode: d.terminalCode,
        deviceCode: d.deviceCode,
        identityKey: d.identityKey,
        subDeviceCode: d.subDeviceCode || d._subCode || undefined,
        interval: this.query.interval,
        pageNum: this.detailQuery.pageNum,
        pageSize: this.detailQuery.pageSize,
        ...extra
      }
    },
    async loadDetail(loadSummary) {
      if (!this.detail) return
      const clicked = {
        subDeviceCode: this.detail.subDeviceCode || this.detail._subCode || '',
        subDeviceLabel: this.detail.subDeviceLabel || this.detail._subLabel || ''
      }
      if (loadSummary) this.detailLoading = true
      this.detailRowsLoading = true
      try {
        const res = await gasApi.reportFlowCustom.detail(this.detailParams())
        const data = res.data || {}
        this.detail = Object.assign({}, this.detail, data.detail || {}, {
          subDeviceCode: (data.detail && data.detail.subDeviceCode) || clicked.subDeviceCode,
          subDeviceLabel: (data.detail && data.detail.subDeviceLabel) || clicked.subDeviceLabel
        })
        this.detailList = data.records || []
        this.detailTotal = Number(data.total || 0)
      } finally {
        this.detailLoading = false
        this.detailRowsLoading = false
      }
    },
    handleDetailSizeChange(size) {
      this.detailQuery.pageSize = size
      this.detailQuery.pageNum = 1
      this.loadDetail(false)
    },
    handleDetailCurrentChange(page) {
      this.detailQuery.pageNum = page
      this.loadDetail(false)
    },
    onDetailClosed() {
      this.detail = null
      this.detailList = []
      this.detailTotal = 0
      this.detailQuery.pageNum = 1
      this.detailQuery.pageSize = 10
    },
    async exportDetail() {
      if (!this.detail) return this.$message.warning('暂无可导出的详情数据')
      const res = await gasApi.reportFlowCustom.detail(this.detailParams({ pageNum: 1, pageSize: Math.max(this.detailTotal, 10000) }))
      const data = res.data || {}
      const metrics = this.detailMetrics
      const sub = this.detail.subDeviceLabel || this.detail._subLabel || ''
      const filename = `自定义区间每天数据_${this.reportDeviceName(this.detail) || ''}_${sub}_${this.query.beginDate || ''}_${this.query.endDate || ''}`
      if (metrics.length) {
        const records = data.records || []
        const sampled = records.some(r => r && r.sampleTime)
        this.downloadCsv(
          records,
          [sampled ? '采样时间' : '统计日期', '子设备', ...metrics.map(m => this.metricColumnLabel(m)), '状态'],
          r => [sampled ? this.periodLabel(r) : r.reportDate, r.subDeviceLabel || sub, ...metrics.map(m => this.metricValue(r, m)), r.recordStatus],
          filename
        )
        return
      }
      this.downloadPeriodDetailCsv(data.records || [], 'custom', filename)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/report-flow.scss';

.interval-hint {
  margin-left: 6px;
  color: #86909c;
  font-size: 12px;
}
.device-pick-tip {
  margin: -4px 0 8px;
  color: #ff7d00;
  font-size: 12px;
  line-height: 20px;
}
</style>
<style>
.report-table tr.report-summary-row > td {
  background: #f7f8fa !important;
  font-weight: 600;
  color: #1d2129;
}
.report-table tr.report-summary-row.is-site > td {
  background: #eef4ff !important;
}
</style>
